/**
 * Authentication Context
 * Provides authentication state management across the app
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, profileAPI, tokenUtils, getErrorMessage } from '../lib/api';

// Create context
const AuthContext = createContext(null);

/**
 * AuthProvider component
 * Wraps the app and provides authentication state and methods
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  /**
   * Initialize auth state from stored tokens
   */
  const initializeAuth = useCallback(async () => {
    try {
      if (tokenUtils.isAuthenticated()) {
        // Try to get user profile to validate token
        const response = await profileAPI.getProfile();
        const userData = response.data.data;
        setUser(userData);
        tokenUtils.setUser(userData);
      }
    } catch (err) {
      // Token invalid or expired - clear everything
      console.error('Auth initialization failed:', err);
      tokenUtils.clearAll();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  /**
   * Register a new user
   * @param {Object} data - { name, email, password, password_confirm, whatsapp }
   */
  const register = async (data) => {
    setError(null);
    try {
      const response = await authAPI.register(data);
      // Registration successful - user can now login
      return { success: true, data: response.data };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      // Extract field errors from error.details.errors
      const fieldErrors = err.response?.data?.error?.details?.errors;
      return { success: false, error: message, errors: fieldErrors };
    }
  };

  /**
   * Login user
   * @param {Object} data - { email, password }
   */
  const login = async (data) => {
    setError(null);
    try {
      const response = await authAPI.login(data);
      const { access, refresh, session } = response.data.data;
      
      // Store tokens
      tokenUtils.setTokens(access, refresh);
      
      // Store session token - this stable UUID identifies this device's session.
      // It is sent as X-Session-Token on every request so the backend can
      // invalidate this device if another login occurs.
      if (session?.token) {
        tokenUtils.setSessionToken(session.token);
      }
      
      // Fetch user profile
      const profileResponse = await profileAPI.getProfile();
      const userData = profileResponse.data.data;
      tokenUtils.setUser(userData);
      setUser(userData);
      
      return { success: true, data: response.data };
    } catch (err) {
      const message = getErrorMessage(err);
      const code = err.response?.data?.error?.code || err.response?.data?.code;
      setError(message);
      return { success: false, error: message, code };
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    setError(null);
    try {
      const refreshToken = tokenUtils.getRefreshToken();
      if (refreshToken) {
        await authAPI.logout(refreshToken);
      }
    } catch (err) {
      // Ignore logout errors - we'll clear tokens anyway
      console.error('Logout error:', err);
    } finally {
      // Always clear local state (tokenUtils.clearAll also removes session_token)
      tokenUtils.clearAll();
      setUser(null);
      navigate('/login');
    }
  };

  /**
   * Request password reset
   * @param {string} email - User's email
   */
  const forgotPassword = async (email) => {
    setError(null);
    try {
      const response = await authAPI.forgotPassword(email);
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      return { success: false, error: message };
    }
  };

  /**
   * Reset password with token
   * @param {Object} data - { uid, token, new_password, new_password_confirm }
   */
  const resetPassword = async (data) => {
    setError(null);
    try {
      const response = await authAPI.resetPassword(data);
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      return { success: false, error: message };
    }
  };

  /**
   * Change password (authenticated)
   * @param {Object} data - { old_password, new_password, new_password_confirm }
   */
  const changePassword = async (data) => {
    setError(null);
    try {
      const response = await authAPI.changePassword(data);
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      return { success: false, error: message };
    }
  };

  /**
   * Update user profile
   * @param {Object} data - Profile data to update
   */
  const updateProfile = async (data) => {
    setError(null);
    try {
      const response = await profileAPI.updateProfile(data);
      const userData = response.data.data;
      tokenUtils.setUser(userData);
      setUser(userData);
      return { success: true, data: userData };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      return { success: false, error: message, errors: err.response?.data?.errors };
    }
  };

  /**
   * Refresh user profile from server
   */
  const refreshProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      const userData = response.data.data;
      tokenUtils.setUser(userData);
      setUser(userData);
      return { success: true, data: userData };
    } catch (err) {
      const message = getErrorMessage(err);
      return { success: false, error: message };
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    updateProfile,
    refreshProfile,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth hook
 * Access authentication context from any component
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * ProtectedRoute component
 * Redirects to login if user is not authenticated
 */
export function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return isAuthenticated ? children : null;
}

export default AuthContext;
